import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { ATTACK_TYPE_LABELS } from '@/services/cyber-threats';
import type { CyberThreat } from '@/types';

// ---------------------------------------------------------------------------
// Cyber Threat Actor Reference — curated OSINT snapshot (Mar 2026)
// ---------------------------------------------------------------------------

const SEVERITY_COLORS = {
  critical: 'text-red-400 border-red-900/50 bg-red-950/30',
  high:     'text-orange-400 border-orange-900/50 bg-orange-950/20',
  medium:   'text-yellow-400 border-yellow-900/40 bg-yellow-950/20',
  low:      'text-emerald-400 border-emerald-900/40 bg-emerald-950/10',
  info:     'text-slate-400 border-slate-700 bg-slate-900/20',
};

const ThreatRow: FC<{ threat: CyberThreat }> = ({ threat }) => {
  const [expanded, setExpanded] = useState(false);
  const colorClass = SEVERITY_COLORS[threat.severity] ?? SEVERITY_COLORS.info;

  return (
    <div
      className={clsx('border rounded p-2 cursor-pointer transition-colors hover:bg-surface-hover/30', colorClass)}
      onClick={() => setExpanded((v) => !v)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">
              {ATTACK_TYPE_LABELS[threat.type] ?? threat.type}
            </span>
            {!threat.active && (
              <span className="text-[8px] px-1 rounded bg-slate-800 text-slate-500">RESOLVED</span>
            )}
          </div>
          <div className="text-[11px] font-semibold text-slate-100 leading-tight mt-0.5 truncate">
            {threat.name}
          </div>
          <div className="text-[10px] text-slate-400 truncate">
            <span className="text-red-400">{threat.threatActor}</span>
            {' → '}{threat.targetCountry}
          </div>
        </div>
        <span className={clsx(
          'shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded uppercase',
          threat.severity === 'critical' ? 'bg-red-900/60 text-red-300' :
          threat.severity === 'high'     ? 'bg-orange-900/60 text-orange-300' :
          'bg-yellow-900/60 text-yellow-300',
        )}>
          {threat.severity}
        </span>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-2 space-y-1.5 border-t border-current/10 pt-2">
          <p className="text-[10px] text-slate-300 leading-relaxed">{threat.description}</p>
          <div className="flex flex-wrap gap-1">
            {threat.affectedSectors.map((s) => (
              <span key={s} className="text-[8px] px-1 rounded bg-slate-800 text-slate-400">{s}</span>
            ))}
          </div>
          {threat.cveIds && threat.cveIds.length > 0 && (
            <div className="text-[9px] text-purple-300">
              CVE: {threat.cveIds.join(', ')}
            </div>
          )}
          <div className="text-[9px] text-slate-500">
            IOCs: {threat.iocs.slice(0, 2).join(' • ')}
            {threat.iocs.length > 2 && ` +${threat.iocs.length - 2} more`}
          </div>
        </div>
      )}
    </div>
  );
};

export const CyberPanel: FC = () => {
  const cyberThreats = useAppStore((s) => s.cyberThreats);

  const sorted = [...cyberThreats].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    return (order[a.severity] ?? 4) - (order[b.severity] ?? 4);
  });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400">⚡ Threat Actors</span>
        <span className="text-[9px] text-slate-500">Curated OSINT · as of Mar 2026</span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-1 px-3 shrink-0 mt-1">
        {(['critical', 'high', 'medium'] as const).map((sev) => {
          const count = cyberThreats.filter((t) => t.severity === sev).length;
          return (
            <div key={sev} className={clsx(
              'rounded p-1.5 text-center border',
              sev === 'critical' ? 'bg-red-950/30 border-red-900/40' :
              sev === 'high'     ? 'bg-orange-950/20 border-orange-900/40' :
              'bg-yellow-950/20 border-yellow-900/40',
            )}>
              <div className={clsx(
                'text-base font-bold tabular-nums',
                sev === 'critical' ? 'text-red-400' : sev === 'high' ? 'text-orange-400' : 'text-yellow-400',
              )}>{count}</div>
              <div className="text-[8px] text-slate-500 uppercase">{sev}</div>
            </div>
          );
        })}
      </div>

      {/* Threat list */}
      <div className="overflow-y-auto flex-1 px-3 pb-2 space-y-1.5 mt-2">
        {sorted.map((threat) => (
          <ThreatRow key={threat.id} threat={threat} />
        ))}
      </div>
    </div>
  );
};
